import React, { useState } from 'react';
import { useTranslation } from '../../LanguageContext';
import styles from './Form.module.css';
import narkis from "../../images/נרקיס תדמיתית.webp"
import PrivacyPolicy from '../privacy/privacy';

const Form = () => {
  const { t, isEnglish } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    reason: '',
    expansion: ''
  });
  const [errors, setErrors] = useState({
    fullName: '',
    phone: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const serverUrl = "https://dynamic-server-dfc88e1f1c54.herokuapp.com/leads/newLead";
  const reciver = "nbnarkisbarazani@gmail.com";

  // Handle privacy link click - prevent form submission
  const handlePrivacyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Form validation
  const validateForm = () => {
    if (!agreed) {
      alert(t.form.privacyAlert);
      return false;
    }

    let valid = true;
    const newErrors = { fullName: '', phone: '', reason: '' };

    if (!formData.fullName.trim()) {
      newErrors.fullName = t.form.errorFullName;
      valid = false;
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = t.form.errorFullNameMin;
      valid = false;
    }

    const phoneRegex = isEnglish ? /^[\d\s\-+()]{7,20}$/ : /^0(5\d|[23489])\d{7}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = t.form.errorPhone;
      valid = false;
    } else if (!phoneRegex.test(formData.phone.trim())) {
      newErrors.phone = t.form.errorPhoneInvalid;
      valid = false;
    }

    if (!formData.reason.trim()) {
      newErrors.reason = t.form.errorReason;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const serverData = {
      name: formData.fullName,
      phone: formData.phone,
      email: "",
      reason: formData.expansion ? `${formData.reason} - ${formData.expansion}` : formData.reason,
      reciver: reciver
    };

    try {
      const serverResponse = await fetch(serverUrl, {
        method: "POST",
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(serverData)
      });

      if (serverResponse.ok) {
        setIsSubmitting(false);
        setSubmitted(true);
        
        setTimeout(() => {
          setFormData({
            fullName: '',
            phone: '',
            reason: '',
            expansion: ''
          });
          setErrors({
            fullName: '',
            phone: '',
            reason: ''
          });
          setSubmitted(false);
          setAgreed(false);
        }, 4000);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      alert(t.form.errorText);
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  return (
    <>
      <section className={styles.formSection}>
        {/* Image Side - Left - Full Height */}
        <div className={styles.imageSide}>
          <img 
            src={narkis}
            alt="Interior Design"
            className={styles.sideImage}
          loading="lazy" decoding="async" />
          <div className={styles.imageOverlay}></div>
        </div>

        {/* Form Side - Right */}
        <div className={styles.formSide}>
          <div className={styles.formWrapper}>
            {/* Header */}
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>{t.form.title}</h2>
              <p className={styles.formSubtitle}>
                {t.form.subtitle}
              </p>
            </div>

            {/* Success Message */}
            {submitted && (
              <div className={styles.successMessage}>
                <div className={styles.successIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                </div>
                <h3>{t.form.successTitle}</h3>
                <p>{t.form.successText}</p>
              </div>
            )}

            {/* Form */}
            {!submitted && (
              <form onSubmit={handleSubmit} className={styles.form}>
                {/* Full Name */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>{t.form.fullName}</span>
                    <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`${styles.input} ${errors.fullName ? styles.inputError : ''}`}
                      placeholder={t.form.fullNamePlaceholder}
                      disabled={isSubmitting}
                    />
                    <div className={styles.inputIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                  </div>
                  {errors.fullName && <span className={styles.errorText}>{errors.fullName}</span>}
                </div>

                {/* Phone */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>{t.form.phone}</span>
                    <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.inputWrapper}>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                      placeholder={t.form.phonePlaceholder}
                      disabled={isSubmitting}
                    />
                    <div className={styles.inputIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                    </div>
                  </div>
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>

                {/* Reason */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>{t.form.reason}</span>
                    <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.inputWrapper}>
                    <select
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      className={`${styles.select} ${errors.reason ? styles.inputError : ''}`}
                      disabled={isSubmitting}
                    >
                      <option value="">{t.form.reasonPlaceholder}</option>
                      {t.form.reasonOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <div className={styles.selectIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6,9 12,15 18,9"/>
                      </svg>
                    </div>
                  </div>
                  {errors.reason && <span className={styles.errorText}>{errors.reason}</span>}
                </div>

                {/* Expansion */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>{t.form.expansion}</span>
                    <span className={styles.optional}>{t.form.optional}</span>
                  </label>
                  <textarea
                    name="expansion"
                    value={formData.expansion}
                    onChange={handleInputChange}
                    className={styles.textarea}
                    placeholder={t.form.expansionPlaceholder}
                    rows="4"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Privacy Checkbox - Same structure as working form */}
                <div className={styles.checkboxContainer}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={() => setAgreed(!agreed)}
                      className={styles.checkbox}
                      disabled={isSubmitting}
                    />
                    {t.form.privacyPrefix}
                    <span onClick={handlePrivacyClick}>
                      <PrivacyPolicy
                        ownerName={isEnglish ? "Narkis Rubin Barazani" : "נרקיס רובין ברזני"}
                        email="nbnarkisbarazani@gmail.com"
                        phone="+972544998929"
                        domain="https://narkisrubinbarazani.co.il/"
                      />
                    </span>
                    {t.form.privacySuffix}
                  </label>
                </div>

                {/* Submit Button */}
                <button 
                  type="submit"
                  className={`${styles.submitBtn} ${isSubmitting ? styles.submitting : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className={styles.spinner}></span>
                      <span>{t.form.submittingText}</span>
                    </>
                  ) : (
                    <>
                      <span>{t.form.submitText}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22,2 15,22 11,13 2,9"/>
                      </svg>
                    </>
                  )}
                </button>

                {/* Alternative Contact */}
                <div className={styles.alternativeContact}>
                  <span className={styles.orText}>{t.form.orContact}</span>
                  <div className={styles.contactLinks}>
                    <a 
                      href="https://wa.me/972544998929?text=היי,%20אשמח%20לשמוע%20עוד%20על%20השירותים%20שלכם" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.contactLink}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span>WhatsApp</span>
                    </a>
                    <a href="tel:0544998929" className={styles.contactLink}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                      <span>054-499-8929</span>
                    </a>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Form;